import { Body, Controller, Get, Headers, HttpCode, Param, Post, Query, Res, UseGuards } from '@nestjs/common';
import { Throttle } from '@nestjs/throttler';
import {
  ApiBearerAuth,
  ApiBody,
  ApiOperation,
  ApiResponse,
  ApiTags,
} from '@nestjs/swagger';
import { Response } from 'express';
import {
  ApplicationStatusResponse,
  AuthResponse,
  AuthUser,
  CheckEmailStatusResponse,
  MyStoresResponse,
  ForgotPasswordRequest,
  LoginRequest,
  RegisterMerchantRequest,
  RegisterMerchantResponse,
  RegisterRequest,
  RegisterResponse,
  ResendVerificationRequest,
  ResetPasswordRequest,
  UserRole,
  VerifyEmailRequest,
} from '@cart/shared-types';
import { AuthGuard } from './auth.guard';
import { AuthService } from './auth.service';
import { GoogleOAuthIntent, GoogleOAuthService } from './google-oauth.service';

@ApiTags('Auth')
@Controller('auth')
export class AuthController {
  constructor(
    private readonly authService: AuthService,
    private readonly googleOAuth: GoogleOAuthService
  ) {}

  @Post('register')
  @Throttle({ default: { ttl: 60000, limit: 5 } })
  @ApiOperation({ summary: 'Register a new customer account — email verification required before login' })
  @ApiBody({
    schema: {
      type: 'object',
      required: ['email', 'password', 'name'],
      properties: {
        email: { type: 'string', example: 'customer@example.com' },
        password: { type: 'string', minLength: 8, example: 'secret123' },
        name: { type: 'string', example: 'Jane Doe' },
        returnOrigin: { type: 'string', example: 'http://localhost:5102' },
      },
    },
  })
  @ApiResponse({ status: 201, description: 'Account created, verification email sent — no accessToken yet' })
  @ApiResponse({ status: 400, description: 'Validation error (missing/invalid fields)' })
  @ApiResponse({ status: 409, description: 'Email already registered' })
  register(@Body() body: RegisterRequest): Promise<RegisterResponse> {
    return this.authService.register(body);
  }

  @Post('register-merchant')
  @Throttle({ default: { ttl: 60000, limit: 5 } })
  @ApiOperation({ summary: 'Register a new merchant + organization — email verification required before login' })
  @ApiBody({
    schema: {
      type: 'object',
      required: ['email', 'password', 'name', 'businessName', 'businessType', 'phone', 'plan'],
      properties: {
        email: { type: 'string', example: 'owner@freshmart.in' },
        password: { type: 'string', minLength: 8, example: 'secret123' },
        name: { type: 'string', example: 'Arjun Mehta' },
        businessName: { type: 'string', example: 'FreshMart' },
        businessType: { type: 'string', example: 'Food & Beverage' },
        phone: { type: 'string', example: '+91 98765 43210' },
        plan: { type: 'string', enum: ['free', 'basic', 'premium'], example: 'free' },
        gstin: { type: 'string', example: '29ABCDE1234F1Z5' },
        city: { type: 'string', example: 'Bengaluru' },
        state: { type: 'string', example: 'Karnataka' },
        returnOrigin: { type: 'string', example: 'http://localhost:5100' },
      },
    },
  })
  @ApiResponse({ status: 201, description: 'Organization + merchant account created, verification email sent' })
  @ApiResponse({ status: 400, description: 'Validation error (missing/invalid fields)' })
  @ApiResponse({ status: 409, description: 'Email already registered' })
  registerMerchant(@Body() body: RegisterMerchantRequest): Promise<RegisterMerchantResponse> {
    return this.authService.registerMerchant(body);
  }

  @Get('check-email')
  @Throttle({ default: { ttl: 60000, limit: 20 } })
  @ApiOperation({
    summary:
      'Live email-availability check for a registration form (blur-of-field, before submit). ' +
      'Reveals registration state on purpose — same enumeration tradeoff as forgot-password.',
  })
  @ApiResponse({ status: 200, description: "Returns { status: 'available' | 'unverified' | 'taken' }" })
  checkEmail(
    @Query('email') email: string | undefined,
    @Query('role') role: UserRole | undefined
  ): Promise<CheckEmailStatusResponse> {
    return this.authService.checkEmailStatus(email ?? '', role ?? 'merchant');
  }

  @Post('verify-email')
  @Throttle({ default: { ttl: 60000, limit: 10 } })
  @ApiOperation({ summary: 'Verify an account with the token from the verification email — logs the account in' })
  @ApiBody({ schema: { type: 'object', required: ['token'], properties: { token: { type: 'string' } } } })
  @ApiResponse({ status: 200, description: 'Verified — returns user + accessToken' })
  @ApiResponse({ status: 400, description: 'Invalid or expired token' })
  verifyEmail(@Body() body: VerifyEmailRequest): Promise<AuthResponse> {
    return this.authService.verifyEmail(body);
  }

  @Post('resend-verification')
  @Throttle({ default: { ttl: 60000, limit: 5 } })
  @HttpCode(204)
  @ApiOperation({ summary: 'Resend the verification email — 404s if the email is not registered' })
  @ApiBody({
    schema: {
      type: 'object',
      required: ['email', 'returnOrigin'],
      properties: {
        email: { type: 'string', example: 'customer@example.com' },
        returnOrigin: { type: 'string', example: 'http://localhost:5102' },
      },
    },
  })
  @ApiResponse({ status: 204, description: 'Verification email sent (or account was already verified — same response either way)' })
  @ApiResponse({ status: 404, description: 'No account found with that email' })
  resendVerification(@Body() body: ResendVerificationRequest): Promise<void> {
    return this.authService.resendVerification(body);
  }

  @Get('my-stores')
  @UseGuards(AuthGuard)
  @ApiBearerAuth()
  @ApiOperation({
    summary:
      'Which stores does the authenticated customer have an account at — backs the ' +
      'sp-account.forgestack.in "which store" picker shown after a global login.',
  })
  @ApiResponse({ status: 200, description: "Returns { stores: [{ name, domain, role }] } — empty array if none" })
  @ApiResponse({ status: 401, description: 'Token missing or expired' })
  myStores(@Headers('authorization') authorization: string | undefined): Promise<MyStoresResponse> {
    const token = authorization?.replace(/^Bearer\s+/i, '') ?? '';
    const payload = this.authService.verifyToken(token);

    return this.authService.myStores(payload.sub);
  }

  @Post('login')
  // No route-specific override — inherits the platform-wide 1000 req/min
  // default (see app.module.ts). Used to be its own tighter 5, but the admin
  // dev-switcher (switchDevUser) does a real login on every click, and a few
  // quick clicks through it was enough to trip that. Keeping one shared
  // number instead of scattered per-route tweaks.
  @ApiOperation({ summary: 'Login with email and password' })
  @ApiBody({
    schema: {
      type: 'object',
      required: ['email', 'password'],
      properties: {
        email: { type: 'string', example: 'customer@example.com' },
        password: { type: 'string', example: 'secret123' },
      },
    },
  })
  @ApiResponse({ status: 200, description: 'Login successful — returns user + accessToken' })
  @ApiResponse({ status: 401, description: 'Invalid email or password' })
  @ApiResponse({ status: 403, description: 'Email not verified yet' })
  login(@Body() body: LoginRequest): Promise<AuthResponse> {
    return this.authService.login(body);
  }

  @Post('forgot-password')
  @Throttle({ default: { ttl: 60000, limit: 5 } })
  @HttpCode(204)
  @ApiOperation({ summary: 'Request a password reset email — 404s if the email is not registered' })
  @ApiBody({
    schema: {
      type: 'object',
      required: ['email', 'returnOrigin'],
      properties: {
        email: { type: 'string', example: 'customer@example.com' },
        returnOrigin: { type: 'string', example: 'http://localhost:5102' },
      },
    },
  })
  @ApiResponse({ status: 204, description: 'Reset email sent' })
  @ApiResponse({ status: 404, description: 'No account found with that email' })
  forgotPassword(@Body() body: ForgotPasswordRequest): Promise<void> {
    return this.authService.forgotPassword(body);
  }

  @Post('reset-password')
  @Throttle({ default: { ttl: 60000, limit: 5 } })
  @HttpCode(204)
  @ApiOperation({ summary: 'Reset a password using a token from the forgot-password email' })
  @ApiBody({
    schema: {
      type: 'object',
      required: ['token', 'newPassword'],
      properties: {
        token: { type: 'string' },
        newPassword: { type: 'string', minLength: 8 },
      },
    },
  })
  @ApiResponse({ status: 204, description: 'Password updated' })
  @ApiResponse({ status: 400, description: 'Invalid or expired token, or weak password' })
  resetPassword(@Body() body: ResetPasswordRequest): Promise<void> {
    return this.authService.resetPassword(body);
  }

  @Get('application-status/:id')
  @Throttle({ default: { ttl: 60000, limit: 30 } })
  @ApiOperation({
    summary:
      'Public, no-auth lookup for the shareable /application-status/:id admin page — works whether or ' +
      'not the viewer is currently logged in. Returns only non-sensitive fields (no email/phone/gstin).',
  })
  @ApiResponse({ status: 200, description: 'Returns businessName, approvalStatus, rejectionReason, plan' })
  @ApiResponse({ status: 404, description: 'No organization with this id' })
  getApplicationStatus(@Param('id') id: string): Promise<ApplicationStatusResponse> {
    return this.authService.getApplicationStatus(id);
  }

  @Get('me')
  @UseGuards(AuthGuard)
  @ApiBearerAuth()
  @ApiOperation({ summary: 'Get the currently authenticated user' })
  @ApiResponse({ status: 200, description: 'Returns authenticated user profile' })
  @ApiResponse({ status: 401, description: 'Token missing or expired' })
  me(@Headers('authorization') authorization: string | undefined): Promise<AuthUser> {
    const token = authorization?.replace(/^Bearer\s+/i, '') ?? '';

    return this.authService.me(token);
  }

  @Post('refresh')
  @UseGuards(AuthGuard)
  @ApiBearerAuth()
  @ApiOperation({ summary: 'Issue a fresh 1-hour JWT from a still-valid token' })
  @ApiResponse({ status: 200, description: 'Returns new accessToken' })
  @ApiResponse({ status: 401, description: 'Token missing or expired' })
  refresh(@Headers('authorization') authorization: string | undefined): Promise<AuthResponse> {
    const token = authorization?.replace(/^Bearer\s+/i, '') ?? '';

    return this.authService.refresh(token);
  }

  /**
   * Deliberately NOT behind AuthGuard: the whole point is to work once the
   * access token has expired, which is exactly when that guard would reject
   * the request. The refresh token in the body is the credential, and
   * SessionService verifies it — rejecting unknown, expired and already-
   * rotated tokens, and revoking the entire family if one is replayed.
   */
  @Post('session/refresh')
  @ApiOperation({ summary: 'Exchange a rotating refresh token for a new access token' })
  @ApiResponse({ status: 200, description: 'Returns a new accessToken and a rotated refreshToken' })
  @ApiResponse({ status: 401, description: 'Refresh token unknown, expired, revoked or replayed' })
  sessionRefresh(@Body() body: { refreshToken?: string }): Promise<AuthResponse> {
    return this.authService.refreshSession(body?.refreshToken ?? '');
  }

  /**
   * Real logout: ends the session server-side so the refresh token stops
   * working. Previously logout only cleared localStorage, leaving the session
   * usable by anyone holding a copy of the token.
   */
  @Post('session/logout')
  @ApiOperation({ summary: 'Revoke the current session' })
  @ApiResponse({ status: 200, description: 'Session revoked (idempotent)' })
  async sessionLogout(@Body() body: { refreshToken?: string }): Promise<{ ok: true }> {
    await this.authService.endSession(body?.refreshToken ?? '');
    return { ok: true };
  }

  @Get('google')
  @ApiOperation({
    summary:
      "Start Google OAuth login — redirects to Google's consent screen. " +
      "intent=merchant restricts the callback to already-registered, verified merchants.",
  })
  googleStart(
    @Query('returnOrigin') returnOrigin: string | undefined,
    @Query('intent') intent: GoogleOAuthIntent | undefined,
    @Res() res: Response
  ): void {
    res.redirect(this.googleOAuth.buildAuthorizeUrl(returnOrigin, intent === 'merchant' ? 'merchant' : 'customer'));
  }

  @Get('google/callback')
  @ApiOperation({ summary: 'Google OAuth callback — issues a JWT and redirects back to the app' })
  async googleCallback(
    @Query('code') code: string | undefined,
    @Query('state') state: string | undefined,
    @Res() res: Response
  ): Promise<void> {
    const { origin, intent } = this.googleOAuth.resolveState(state);

    if (!code) {
      res.redirect(`${origin}/login?error=google`);
      return;
    }

    try {
      const profile = await this.googleOAuth.exchangeCode(code);
      const auth =
        intent === 'merchant'
          ? await this.authService.loginWithGoogleMerchant(profile)
          : await this.authService.loginWithGoogle(profile);
      res.redirect(`${origin}/auth/callback?token=${encodeURIComponent(auth.accessToken)}`);
    } catch {
      res.redirect(`${origin}/login?error=google`);
    }
  }

  @Post('logout')
  @UseGuards(AuthGuard)
  @HttpCode(204)
  @ApiBearerAuth()
  @ApiOperation({ summary: 'Stateless logout — server confirms the token, client clears it' })
  @ApiResponse({ status: 204, description: 'Logout accepted' })
  @ApiResponse({ status: 401, description: 'Token missing or expired' })
  logout(): void {
    // JWT is stateless — client discards the token. Server confirms the token was valid.
  }
}
